import { useState } from "react";
import axios from "axios";
import { API } from "../../../App";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, ACCOUNT_SUBTYPES } from "./accountingUtils";

const AccountsList = ({ accounts, isDataEntry, onRefresh }) => {
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({
    account_code: "",
    account_name: "",
    account_type: "asset",
    account_subtype: "current_asset",
    description: "",
    opening_balance: 0,
  });

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounting/accounts`, newAccount);
      toast.success("Account created");
      setShowNewAccount(false);
      setNewAccount({
        account_code: "",
        account_name: "",
        account_type: "asset",
        account_subtype: "current_asset",
        description: "",
        opening_balance: 0,
      });
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    }
  };

  return (
    <div className="space-y-4" data-testid="accounts-list">
      {!isDataEntry && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setShowNewAccount(true)} data-testid="new-account-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Account
          </Button>
        </div>
      )}

      {["asset", "liability", "equity", "revenue", "expense"].map((type) => {
        const typeAccounts = accounts.filter((a) => a.account_type === type);
        if (typeAccounts.length === 0) return null;
        return (
          <Card key={type}>
            <CardHeader>
              <CardTitle className="capitalize">{type}s</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Account Name</th>
                      <th className="text-left p-2">Subtype</th>
                      <th className="text-right p-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeAccounts.map((acc) => (
                      <tr key={acc.account_id} className="border-b last:border-0">
                        <td className="p-2 font-mono">{acc.account_code}</td>
                        <td className="p-2">{acc.account_name}</td>
                        <td className="p-2 capitalize">{acc.account_subtype.replace(/_/g, " ")}</td>
                        <td className="p-2 text-right font-medium">{formatCurrency(acc.current_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {showNewAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>New Account</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Account Code *</label>
                    <Input
                      value={newAccount.account_code}
                      onChange={(e) => setNewAccount({ ...newAccount, account_code: e.target.value })}
                      placeholder="e.g., 1050"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Account Type *</label>
                    <select
                      value={newAccount.account_type}
                      onChange={(e) =>
                        setNewAccount({
                          ...newAccount,
                          account_type: e.target.value,
                          account_subtype: ACCOUNT_SUBTYPES[e.target.value][0],
                        })
                      }
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {["asset", "liability", "equity", "revenue", "expense"].map((t) => (
                        <option key={t} value={t} className="capitalize">{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Account Name *</label>
                  <Input
                    value={newAccount.account_name}
                    onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                    placeholder="Account name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subtype *</label>
                  <select
                    value={newAccount.account_subtype}
                    onChange={(e) => setNewAccount({ ...newAccount, account_subtype: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {ACCOUNT_SUBTYPES[newAccount.account_type]?.map((st) => (
                      <option key={st} value={st} className="capitalize">{st.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Opening Balance</label>
                  <Input
                    type="number"
                    value={newAccount.opening_balance}
                    onChange={(e) => setNewAccount({ ...newAccount, opening_balance: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    value={newAccount.description}
                    onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowNewAccount(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">Create Account</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AccountsList;
